import Grid from '@mui/material/Grid'
import capitalize from 'lodash/capitalize'
import React from 'react'

import { DEFAULT_STATE } from 'components/common/address/model'
import { selectAddress } from 'data/address/addressSelectors'
import T from 'T'

import PreviewField from '../common/PreviewField'

const {
  addressNamePlaceholder,
  line1Placeholder,
  line2Placeholder,
  parcelNumberPlaceholder,
  cityPlaceholder,
  statePlaceholder,
  zipCodePlaceholder,
} = DEFAULT_STATE

const PreviewFieldUntyped = PreviewField as React.ElementType

type Address = {
  line1?: string
  unitNumber?: string
  city?: string
  state?: string
  zipCode?: string
}

type LocationContentProps = {
  showAddressName?: boolean
  addressName?: string
  addressNameLabel?: string
  address?: Address
  showPlaceholder?: boolean
  showParcelNumber?: boolean
  parcelNumber?: string
  // 2-column field grid (name+street, then street-line-2+city/state/zip) instead of every field
  // stacked full-width — cuts the WO-detail Location accordion's whitespace roughly in half.
  compact?: boolean
}

type AddressFieldsProps = {
  line1?: string
  unitNumber?: string
  city?: string
  state?: string
  zipCode?: string
  showPlaceholder: boolean
  showParcelNumber: boolean
  parcelNumber: string
}

const renderFullAddressFields = ({
  line1,
  unitNumber,
  city,
  state,
  zipCode,
  showPlaceholder,
  showParcelNumber,
  parcelNumber,
}: AddressFieldsProps) => (
  <>
    <Grid size={12}>
      <PreviewFieldUntyped
        fieldName={capitalize(T.STREET_ADDRESS)}
        fieldValue={line1}
        placeholderValue={line1Placeholder}
        showPlaceholder={showPlaceholder}
      />
    </Grid>
    <Grid container size={12} spacing={3}>
      <Grid>
        <PreviewFieldUntyped
          fieldName={T.STREET_LINE_2}
          fieldValue={unitNumber}
          placeholderValue={line2Placeholder}
          showPlaceholder={showPlaceholder}
        />
      </Grid>
      {showParcelNumber && (
        <Grid>
          <PreviewFieldUntyped
            fieldName={T.PARCEL_NUMBER}
            fieldValue={parcelNumber}
            placeholderValue={parcelNumberPlaceholder}
            showPlaceholder={showPlaceholder}
          />
        </Grid>
      )}
    </Grid>
    <Grid container spacing={3}>
      <Grid>
        <PreviewFieldUntyped fieldName={T.CITY} fieldValue={city} placeholderValue={cityPlaceholder} showPlaceholder={showPlaceholder} />
      </Grid>
      <Grid>
        <PreviewFieldUntyped fieldName={T.STATE} fieldValue={state} placeholderValue={statePlaceholder} showPlaceholder={showPlaceholder} />
      </Grid>
      <Grid>
        <PreviewFieldUntyped
          fieldName={capitalize(T.ZIP_CODE)}
          fieldValue={zipCode}
          placeholderValue={zipCodePlaceholder}
          showPlaceholder={showPlaceholder}
        />
      </Grid>
    </Grid>
  </>
)

// 2-column compact layout: [name | street] then [street line 2 | city,state,zip] — uses the row's
// full width instead of stacking 5 full-width label/value blocks (which leaves a lot of dead space
// in a narrow accordion column).
const renderCompactAddressFields = ({
  line1,
  unitNumber,
  city,
  state,
  zipCode,
  showPlaceholder,
  showParcelNumber,
  parcelNumber,
}: AddressFieldsProps) => {
  const cityStateZipLine = [city, [state, zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')

  return (
    <>
      <Grid size={6}>
        <PreviewFieldUntyped
          fieldName={capitalize(T.STREET_ADDRESS)}
          fieldValue={line1}
          placeholderValue={line1Placeholder}
          showPlaceholder={showPlaceholder}
        />
      </Grid>
      <Grid size={6}>
        <PreviewFieldUntyped
          fieldName={T.STREET_LINE_2}
          fieldValue={unitNumber}
          placeholderValue={line2Placeholder}
          showPlaceholder={showPlaceholder}
        />
      </Grid>
      <Grid size={6}>
        <PreviewFieldUntyped fieldName={T.CITY_STATE_ZIP} fieldValue={cityStateZipLine} showPlaceholder={showPlaceholder} />
      </Grid>
      {showParcelNumber && (
        <Grid size={6}>
          <PreviewFieldUntyped
            fieldName={T.PARCEL_NUMBER}
            fieldValue={parcelNumber}
            placeholderValue={parcelNumberPlaceholder}
            showPlaceholder={showPlaceholder}
          />
        </Grid>
      )}
    </>
  )
}

const LocationContent: React.FC<LocationContentProps> = ({
  showAddressName = false,
  addressName = '',
  addressNameLabel = capitalize(T.SERVICE_LOCATION_NAME),
  address = {},
  showPlaceholder = false,
  showParcelNumber = false,
  parcelNumber = '',
  compact = false,
}) => {
  const { line1, unitNumber, city, state, zipCode } = selectAddress({ ...address })
  const addressFieldsProps = { line1, unitNumber, city, state, zipCode, showPlaceholder, showParcelNumber, parcelNumber }

  return (
    <Grid container columnSpacing={compact ? 3 : 0} rowGap={compact ? 1 : 1.5} sx={{ width: '100%' }}>
      {showAddressName && (
        <Grid size={compact ? 6 : 12}>
          <PreviewFieldUntyped
            fieldName={addressNameLabel}
            fieldValue={addressName}
            fontWeight={500}
            placeholderValue={addressNamePlaceholder}
            showPlaceholder={showPlaceholder}
          />
        </Grid>
      )}
      {compact ? renderCompactAddressFields(addressFieldsProps) : renderFullAddressFields(addressFieldsProps)}
    </Grid>
  )
}

export default LocationContent
